import {
  ReductionMeasureListModel,
  ReductionMeasureModel,
} from "object-models/ReductionMeasures";
import { GHGEmissionFactorModel } from "object-models/GHGEmissionFactorModel";

import apiHandler from "../apiHandler";

export interface ReductionMeasureTemplateDownloadResponse {
  blob: Blob;
  fileName?: string;
}

interface ReductionMeasureMaxEndYearResponse {
  maxEndYear: number | null;
}

const getFileNameFromContentDisposition = (
  contentDisposition?: string
): string | undefined => {
  if (!contentDisposition) {
    return undefined;
  }

    const utf8FileNameMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8FileNameMatch?.[1]) {
    return decodeURIComponent(utf8FileNameMatch[1]).replace(/"/g, "");
  }

    const fileNameMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return fileNameMatch?.[1];
};

export const _getReductionMeasureList = async (locationId: number) => {
  return apiHandler
    .get<Array<ReductionMeasureListModel>>(
      "location/" + locationId + "/reductionMeasures"
    )
    .then((res) => {
      res.forEach((r) => (r.id = r.reductionMeasureId));
      return res;
    });
};

export const _getReductionMeasureMaxEndYear = async (locationId: number) => {
  const response = await apiHandler.get<ReductionMeasureMaxEndYearResponse>(
    `location/${locationId}/reductionMeasures/maxEndYear`
  );

  return response?.maxEndYear ?? null;
};

export const _addReductionMeasure = async (measure: ReductionMeasureModel) => {
  return apiHandler.post<ReductionMeasureModel>("reductionmeasure", measure);
};
export const _updateReductionMeasure = async (
  measure: ReductionMeasureModel
) => {
  return apiHandler.put<ReductionMeasureModel>(
    "updatereductionmeasure",
    measure
  );
};

export const _getReductionMeasure = async (byId: number) => {
  return apiHandler.get<ReductionMeasureModel>("reductionMeasures/" + byId);
};

export const _deleteReductionMeasure = async (reductionMeasureId: number) => {
  return apiHandler.delete("reductionMeasures/" + reductionMeasureId);
};
export const _uploadMeasure = async (formData: FormData) => {
  return apiHandler.post<any>("reductionMeasures/uploadMeasure", formData,'form');
};
export interface OrganizationMeasureUploadResponse {
  success?: boolean;
  message?: string;
  userMessage?: string;
  errorCode?: string;
  failedRows?: number;
  errors?: { message: string }[];
}

export const _uploadOrganizationMeasure = async (
  formData: FormData
): Promise<OrganizationMeasureUploadResponse> => {
  return apiHandler.post<OrganizationMeasureUploadResponse>(
    "organization/reductionMeasures/uploadMeasure",
    formData,
    "form"
  );
};
export const _downloadMeasureTemplate = async () => {
  return apiHandler.get<Blob>("reductionMeasures/template", "blob");
};

export const _downloadReductionMeasuresTemplate = async (
  locationId: number
): Promise<ReductionMeasureTemplateDownloadResponse> => {
  const response = await apiHandler.getWithResponse<Blob>(
    `location/${locationId}/reductionMeasures/template`,
    "blob"
  );

  const contentDisposition =
    typeof (response.headers as any)?.get === "function"
      ? (response.headers as any).get("content-disposition")
      : (response.headers as any)?.["content-disposition"];

  return {
    blob: response.data,
    fileName: getFileNameFromContentDisposition(contentDisposition),
  };
};

export const _downloadOrganizationReductionMeasuresTemplate = async (
  organizationId: number
): Promise<ReductionMeasureTemplateDownloadResponse> => {
  const response = await apiHandler.getWithResponse<Blob>(
    `organization/${organizationId}/reductionMeasures/template`,
    "blob"
  );

  const contentDisposition =
    typeof (response.headers as any)?.get === "function"
      ? (response.headers as any).get("content-disposition")
      : (response.headers as any)?.["content-disposition"];

  return {
    blob: response.data,
    fileName: getFileNameFromContentDisposition(contentDisposition),
  };
};

export const _getGHGEmissionFactors = async (clientId?: number) => {
  const query = clientId && clientId > 0 ? `?clientId=${clientId}` : "";
  return apiHandler.get<Array<GHGEmissionFactorModel>>(
    `ghg-emission-factors${query}`
  );
};

export const _getGHGEmissionFactorUnits = async (params?: {
  ghgType?: string;
  customGHGType?: string;
  reductionMeasureId?: number;
  signal?: AbortSignal;
}) => {
  const query = new URLSearchParams();

  if (params?.ghgType) {
    query.set("ghgType", params.ghgType);
  }
  if (params?.customGHGType) {
    query.set("customGHGType", params.customGHGType);
  }
  if (params?.reductionMeasureId && params.reductionMeasureId > 0) {
    query.set("reductionMeasureId", String(params.reductionMeasureId));
  }

  const queryString = query.toString();
  const unitsUrl =
    "ghg-emission-factors/units" + (queryString ? "?" + queryString : "");
  return apiHandler.get<Array<string>>(
    unitsUrl,
    "json",
    params?.signal
  );
};